import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import Button, { ButtonSize, ButtonType, ButtonVariant } from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { Row } from 'src/components/atoms/Row';
import Text, { TextAlign } from 'src/components/atoms/Text';
import { ControlledDatePicker } from 'src/components/molecules/DatePicker';
import CustomMultiSelectDropdown from 'src/components/folders/ModernizedFraudAnalystReview/components/CustomMultiSelectDropdown';
import {
  dedupeByCode,
  isResolvableReasonCode,
  isStartDateOver90Days,
  reasonCodeLabel,
  validateBalanceField,
  VOA_REASON_CODE_STALE_START,
  VOA_REJECTION_REASON_CODES,
  VOA_STALE_START_WARNING,
  VoaReasonCode
} from 'src/features/assetVerification';
import {
  ChipDismissButton,
  FieldLabel,
  FieldPair,
  RejectControl,
  RejectReasonChip,
  StatementBlock,
  StatementGrid
} from './styles';

type StatementRowProps = {
  // Flat two-segment RHF names ("stmt{ai}_{si}.field") — this codebase's inputs
  // resolve nested errors exactly one dot deep (InputText.tsx errorWithIndicator).
  fieldPrefix: string;
  testIdSuffix: string;
  canRemove: boolean;
  onRemove: () => void;
  reasonCodes?: VoaReasonCode[];
  onResolveReasonCode?: (code: number) => void;
  analystReasonCodes?: number[];
  onChangeAnalystReasonCodes?: (codes: number[]) => void;
  isNinetyDayOverridden?: boolean;
  onOverrideStaleStart?: () => void;
};

// The derived 2023 renders the ticket's analyst-facing sentence, not the generic
// review-level label.
const reasonText = (reason: VoaReasonCode): string =>
  reason.code === VOA_REASON_CODE_STALE_START ? VOA_STALE_START_WARNING : reasonCodeLabel(reason);

const toDate = (value: unknown): Date | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const StatementRow = ({
  fieldPrefix,
  testIdSuffix,
  canRemove,
  onRemove,
  reasonCodes = [],
  onResolveReasonCode,
  analystReasonCodes = [],
  onChangeAnalystReasonCodes,
  isNinetyDayOverridden = false,
  onOverrideStaleStart
}: StatementRowProps) => {
  // FVS can send two details for the same code; one row per code, first copy wins.
  const fvsCodes = useMemo(() => dedupeByCode(reasonCodes), [reasonCodes]);

  // A code FVS already attached is not offered again: it would render both as a
  // warning row and as a selectable option.
  const rejectionOptions = useMemo(() => {
    const attached = new Set(fvsCodes.map((reason) => reason.code));
    return VOA_REJECTION_REASON_CODES.filter((code) => !attached.has(code)).map((code) => ({
      value: String(code),
      label: reasonCodeLabel({ code })
    }));
  }, [fvsCodes]);
  // Feed each picker the sibling field's value so react-datepicker's range
  // props actually do something: the calendar highlights the period and the
  // end picker refuses days before the start (min/maxDate). validateDocument's
  // INVALID_DATE_RANGE covers typed entry the calendar can't constrain.
  const [startValue, endValue] = useWatch({
    name: [`${fieldPrefix}.startDate`, `${fieldPrefix}.endDate`]
  });
  const startDate = toDate(startValue);
  const endDate = toDate(endValue);

  return (
    <StatementBlock>
      {/* Not a live region: it hosts the focusable Resolve buttons, and
        role="status" would re-announce every remaining warning on each
        resolve while hiding the buttons from some AT virtual cursors. */}
      {fvsCodes.length > 0 && (
        <div data-testid={`statement-warnings-${testIdSuffix}`}>
          {fvsCodes.map((reason) => (
            <Row key={reason.code} gap={0.5}>
              <Text align={TextAlign.left}>{`\u26a0 ${reasonText(reason)}`}</Text>
              {/* Resolving removes the code so the statement counts again in FVS's
                mean. 2003 never excludes and 2024 is re-derived server-side, so
                neither gets the affordance. */}
              {reason.code === VOA_REASON_CODE_STALE_START && onOverrideStaleStart && (
                <Button
                  name={`override-stale-start-${testIdSuffix}`}
                  data-testid={`override-stale-start-${testIdSuffix}`}
                  type={ButtonType.button}
                  size={ButtonSize.small}
                  variant={ButtonVariant.ghost}
                  aria-label="Override the 90-day exclusion"
                  onClick={onOverrideStaleStart}
                >
                  Override
                </Button>
              )}
              {isResolvableReasonCode(reason.code) && onResolveReasonCode && (
                <Button
                  name={`resolve-reason-${testIdSuffix}-${reason.code}`}
                  data-testid={`resolve-reason-${testIdSuffix}-${reason.code}`}
                  type={ButtonType.button}
                  size={ButtonSize.small}
                  variant={ButtonVariant.ghost}
                  aria-label={`Resolve: ${reasonText(reason)}`}
                  onClick={() => onResolveReasonCode(reason.code)}
                >
                  Resolve
                </Button>
              )}
            </Row>
          ))}
        </div>
      )}
      {isNinetyDayOverridden && isStartDateOver90Days(String(startValue ?? '')) && (
        <Text align={TextAlign.left} testId={`stale-start-overridden-${testIdSuffix}`}>
          Overridden: this statement starts more than 90 days ago and will be included.
        </Text>
      )}
      {analystReasonCodes.length > 0 && (
        <div data-testid={`statement-rejections-${testIdSuffix}`}>
          {analystReasonCodes.map((code) => (
            <RejectReasonChip key={code} data-testid={`reject-chip-${testIdSuffix}-${code}`}>
              {reasonCodeLabel({ code })}
              {onChangeAnalystReasonCodes && (
                <ChipDismissButton
                  type="button"
                  data-testid={`reject-chip-dismiss-${testIdSuffix}-${code}`}
                  aria-label={`Remove rejection: ${reasonCodeLabel({ code })}`}
                  onClick={() =>
                    onChangeAnalystReasonCodes(analystReasonCodes.filter((c) => c !== code))
                  }
                >
                  ×
                </ChipDismissButton>
              )}
            </RejectReasonChip>
          ))}
        </div>
      )}
      <StatementGrid>
        <div>
          <FieldLabel>
            <Text align={TextAlign.left}>Statement period</Text>
          </FieldLabel>
          {/* mode="type": the default (custom-input) DatePicker recreates its input
            component on every render, so React remounts the field and focus dies
            after the first keystroke. The typeable variant keeps react-datepicker's
            native input, which supports typing and the calendar. */}
          <FieldPair>
            <ControlledDatePicker
              className="asset-verification-date-picker"
              name={`${fieldPrefix}.startDate`}
              mode="type"
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate}
            />
            <ControlledDatePicker
              className="asset-verification-date-picker"
              name={`${fieldPrefix}.endDate`}
              mode="type"
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
            />
          </FieldPair>
        </div>
        <div>
          <FieldLabel>
            <Text align={TextAlign.left}>Starting balance</Text>
          </FieldLabel>
          <InputText
            name={`${fieldPrefix}.startBalance`}
            data-testid={`statement-start-balance-${testIdSuffix}`}
            type={InputTextType.text}
            inputMode="decimal"
            placeholder="$0.00"
            showLabel={false}
            showOptionalLabel={false}
            config={{ validate: validateBalanceField }}
          />
        </div>
        <div>
          <FieldLabel>
            <Text align={TextAlign.left}>Ending balance</Text>
          </FieldLabel>
          <InputText
            name={`${fieldPrefix}.endBalance`}
            data-testid={`statement-end-balance-${testIdSuffix}`}
            type={InputTextType.text}
            inputMode="decimal"
            placeholder="$0.00"
            showLabel={false}
            showOptionalLabel={false}
            config={{ validate: validateBalanceField }}
          />
        </div>
      </StatementGrid>
      {onChangeAnalystReasonCodes && (
        <RejectControl>
          <Gap height={0.5} />
          {/* The dropdown trigger is a div[role="button"], not a labelable
              element, so the name has to flow through aria-labelledby. The
              trigger references itself second because aria-labelledby replaces
              an element's own content in the name: without the self-reference a
              screen reader announces the label but never the current selection
              the sighted analyst can read off the trigger. */}
          <FieldLabel id={`reject-reason-label-${testIdSuffix}`}>
            <Text align={TextAlign.left}>Reject this statement</Text>
          </FieldLabel>
          <CustomMultiSelectDropdown
            id={`reject-reason-${testIdSuffix}`}
            ariaLabelledBy={`reject-reason-label-${testIdSuffix} reject-reason-${testIdSuffix}`}
            value={analystReasonCodes.map(String)}
            onChange={(values) => onChangeAnalystReasonCodes(values.map(Number))}
            options={rejectionOptions}
            placeholder="Select a reason"
            direction="down"
            width="100%"
          />
        </RejectControl>
      )}
      {canRemove && (
        <>
          <Gap height={0.5} />
          <Button
            name={`remove-statement-${testIdSuffix}`}
            data-testid={`remove-statement-${testIdSuffix}`}
            type={ButtonType.button}
            size={ButtonSize.small}
            variant={ButtonVariant.ghost}
            onClick={onRemove}
          >
            Remove statement
          </Button>
        </>
      )}
    </StatementBlock>
  );
};

export default StatementRow;
